import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Phone, Sparkles, ChevronRight, CheckCircle2, Clock, ImageIcon } from "lucide-react";
import type { LeadWithGenerations } from "@/types/firestore";
import { formatDistanceToNow } from "date-fns";
import { Timestamp } from "firebase/firestore";

interface LeadCardProps {
  lead: LeadWithGenerations;
  onViewDetails: (lead: LeadWithGenerations) => void;
  onRemix: (lead: LeadWithGenerations) => void;
}

export function LeadCard({ lead, onViewDetails, onRemix }: LeadCardProps) {
  const [imageError, setImageError] = useState(false);
  const latestGeneration = lead.generations[lead.generations.length - 1];
  // Handle both real Firestore Timestamps and mock Timestamps from API compat layer
  const createdAt = lead.created_at && typeof lead.created_at === 'object' && 'toDate' in lead.created_at
    ? lead.created_at.toDate()
    : new Date(lead.created_at);
  const formattedDate = formatDistanceToNow(createdAt, { addSuffix: true });

  // Capitalize room type
  const formatRoomType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <article aria-labelledby={`lead-${lead.id}`}>
      <Card className="group overflow-hidden border-border/40 bg-card transition-all duration-300 hover:border-accent/30 hover:shadow-lg hover:shadow-accent/5 active:scale-[0.99]">
        {/* Image Section - only show if there's a generated image */}
      {latestGeneration?.generated_image_path && !imageError && (
        <div className="relative aspect-video overflow-hidden bg-muted">
          <img
            src={latestGeneration.generated_image_path}
            alt={`${lead.gemini_analysis?.room_type || 'Room'} renovation for ${lead.client_name}`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setImageError(true)}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/30 to-transparent" />

          {/* Top badges */}
          <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
            <Badge
              variant="secondary"
              className="bg-background/90 backdrop-blur-sm text-xs font-medium shadow-sm"
            >
              {formatRoomType(lead.gemini_analysis?.room_type || 'Unknown')}
            </Badge>
            <Badge
              variant={lead.gemini_analysis?.room_state === "Studs" ? "destructive" : "outline"}
              className="bg-background/90 backdrop-blur-sm text-xs shadow-sm"
            >
              {lead.gemini_analysis?.room_state || 'Unknown'}
            </Badge>
          </div>

        </div>
      )}

      <CardContent className="p-4">
        {/* Client Info Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <h3 id={`lead-${lead.id}`} className="font-heading font-semibold text-foreground text-base truncate">
              {lead.client_name}
            </h3>
            <div className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
              <Phone className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{lead.client_phone}</span>
            </div>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {/* Verification Status */}
          <Badge
            variant="outline"
            className={
              lead.verification_status === "verified"
                ? "text-green-600 bg-green-50 border-green-200"
                : lead.verification_status === "pending"
                ? "text-yellow-600 bg-yellow-50 border-yellow-200"
                : "text-gray-600 bg-gray-50 border-gray-200"
            }
          >
            {lead.verification_status === "verified" ? (
              <>
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Verified
              </>
            ) : lead.verification_status === "pending" ? (
              <>
                <Clock className="h-3 w-3 mr-1" />
                Pending
              </>
            ) : (
              "Unverified"
            )}
          </Badge>

          {/* Callback Requested */}
          {lead.callback_requested && (
            <Badge
              variant="outline"
              className="text-orange-600 bg-orange-50 border-orange-200"
            >
              Callback Requested
            </Badge>
          )}

          {/* Generation Status */}
          {latestGeneration?.generated_image_path ? (
            <Badge
              variant="outline"
              className="text-green-600 bg-green-50 border-green-200"
            >
              <ImageIcon className="h-3 w-3 mr-1" />
              Preview Generated
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-gray-600 bg-gray-50 border-gray-200"
            >
              No Preview
            </Badge>
          )}
        </div>

        {/* AI Notes */}
        {lead.gemini_analysis?.notes && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3 leading-relaxed">
            {lead.gemini_analysis.notes}
          </p>
        )}

        {/* Style Tags */}
        {latestGeneration && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            <Badge variant="outline" className="text-xs bg-accent/5 border-accent/20 text-accent">
              {latestGeneration.prompt_parameters.style}
            </Badge>
            {latestGeneration.prompt_parameters.floor && (
              <Badge variant="outline" className="text-xs">
                {latestGeneration.prompt_parameters.floor}
              </Badge>
            )}
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between border-t border-border/40 pt-3 -mx-4 px-4">
          <span className="text-xs text-muted-foreground">{formattedDate}</span>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemix(lead)}
              className="h-9 px-3 gap-1.5 text-xs hover:text-accent hover:bg-accent/10"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Remix
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewDetails(lead)}
              className="h-9 px-3 gap-1 text-xs bg-primary hover:bg-primary/90 shadow-sm"
            >
              View
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
      </Card>
    </article>
  );
}

export function LeadCardSkeleton() {
  return (
    <Card className="overflow-hidden border-border/40 bg-card">
      <Skeleton className="aspect-video" />
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1">
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-6 w-16" />
        </div>
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4 mb-4" />
        <div className="flex gap-1.5 mb-4">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="flex items-center justify-between border-t border-border/40 pt-3">
          <Skeleton className="h-4 w-20" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-16" />
            <Skeleton className="h-9 w-16" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
